import isNil from "lodash/isNil";
import mapValues from "lodash/mapValues";
import omitBy from "lodash/omitBy";
import pickBy from "lodash/pickBy";
import { areFiltersEqual, hasDefaultValue } from "PFCore/helpers/filters";
import { useSaveLatestFilters } from "PFCore/hooks/queries/filters/use_save_latest_filters";
import { DEFAULT_LATEST_FILTERS_KEY } from "PFCore/services/filters";
import { FiltersV2, FilterV2 } from "PFTypes/filters_v2";

import { useLastUsedFilters } from "../../../hooks/use_last_used_filters";
import { useOnFiltersMetaSync } from "../../../hooks/use_on_filters_meta_sync";
import { useFiltersV2Select } from "../filters_v2_store_context";

const getDefaultFilters = (filters: FiltersV2) =>
  mapValues(pickBy(filters, hasDefaultValue), ({ value, defaultValue, ...filter }: FilterV2) => ({
    ...filter,
    value: defaultValue
  })) as FiltersV2;

const omitLimitedFilters = (filters: FiltersV2) => omitBy(filters, ({ limit }) => !isNil(limit));

export const AutoSaveFiltersEffect = () => {
  const { collection, persistent, id } = useFiltersV2Select("collection", "persistent", "id");

  const { mutate: saveFilters } = useSaveLatestFilters({ target: collection!, key: id });
  const { mutate: saveDefaultFilters } = useSaveLatestFilters({
    target: collection!,
    key: DEFAULT_LATEST_FILTERS_KEY
  });
  const { defaultFilters } = useLastUsedFilters();

  useOnFiltersMetaSync(({ mainFilters, childrenFilters, areChildrenOn }) => {
    if (!collection || !persistent) {
      return;
    }

    const mainFiltersToSave = omitLimitedFilters((mainFilters as FiltersV2) || {});
    const childrenFiltersToSave = areChildrenOn
      ? omitLimitedFilters((childrenFilters as FiltersV2) || {})
      : undefined;

    saveFilters({
      mainFilters: mainFiltersToSave,
      childrenFilters: childrenFiltersToSave
    });
  });

  useOnFiltersMetaSync(({ mainFilters, childrenFilters }) => {
    if (!collection || !persistent) {
      return;
    }

    const newDefaultFilters = {
      mainFilters: getDefaultFilters(mainFilters),
      childrenFilters: getDefaultFilters(childrenFilters)
    };

    if (
      !areFiltersEqual(newDefaultFilters.mainFilters, defaultFilters?.collection.mainFilters || {}) ||
      !areFiltersEqual(newDefaultFilters.childrenFilters, defaultFilters?.collection.childrenFilters || {})
    ) {
      saveDefaultFilters(newDefaultFilters);
    }
  });

  return null;
};
